import { createTheme } from "@mui/material/styles";
import { createBreakpoints } from "@mui/system";

const belowThisBreakpointIsMobile: number | "xs" | "sm" | "md" | "lg" | "xl" = "sm";

export function getBreakpointForMobile() {
    return belowThisBreakpointIsMobile;
}

const lightGrey = "#F7F7F7";
const warmGrey = "#D9D3CC";
const mutedGrey = "#707070";
const white = "#FFFFFF";
const lightLavender = "#E4EAFF";
const lavender = "#B8C7FF";
const darkBlue = "#000000";
const lightBlue = "#F3F0EC";
const darkLavender = "#93A6E8";
const errorRed = "#D32F2F";

export const radioAccentColor = darkLavender;

const ghostButtonStyle = {
    borderColor: "#D3D3D3",
    backgroundColor: white,
    color: mutedGrey,
    boxShadow: "none",
    "&:hover": {
        borderColor: darkBlue,
        backgroundColor: darkBlue,
        color: white,
        boxShadow: "none",
    },
};

declare module "@mui/material/styles/createTheme" {
    interface Theme {
        customPalette: {
            white: string;
            lightBlue: string;
            darkBlue: string;
            errorRed: string;
        };
        customSpacing: {
            iconSize: number;
            contentSpacing: number;
        };
        spacingAsNumber(spacing: number): number;
    }
    // allow configuration using `createMuiTheme`
    interface ThemeOptions {
        customPalette?: {
            white: string;
            lightBlue: string;
            darkBlue: string;
            errorRed: string;
        };
        customSpacing: {
            iconSize: number;
            contentSpacing: number;
        };
        spacingAsNumber(spacing: number): number;
    }
}

const theme = createTheme();

const customBreakpointValues = {
    keys: [...theme.breakpoints.keys],
    values: {
        ...theme.breakpoints.values,
        xl: 1600,
    },
};

const breakpoints = createBreakpoints({ ...customBreakpointValues });

const ipPortalTheme = createTheme({
    spacingAsNumber: (spacing: number) => Number(theme.spacing(spacing).slice(0, -2)),
    breakpoints: { ...breakpoints },
    customPalette: {
        white,
        lightBlue,
        darkBlue,
        errorRed,
    },
    customSpacing: {
        iconSize: 18,
        contentSpacing: 4,
    },
    shape: {
        borderRadius: 20,
    },
    palette: {
        text: {
            primary: darkBlue,
            secondary: mutedGrey,
        },
        grey: {
            A100: lightGrey,
        },
        primary: {
            light: lightBlue,
            main: darkBlue,
            dark: darkBlue /* Fixes DotLegalHeader. It uses primary.dark */,
        },
        secondary: {
            light: lightLavender,
            main: lavender,
            dark: darkLavender,
        },
    },
    typography: {
        fontFamily: ['"Open Sans"'].join(","),
        h5: {
            textTransform: "uppercase",
            color: darkBlue,
            fontSize: "0.75rem",
            fontWeight: theme.typography.fontWeightBold,
        },
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    fontSize: "0.875rem",
                    lineHeight: 1.43,
                    letterSpacing: "0.01071em",
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltipPlacementRight: {
                    margin: "0 !important",
                },
                tooltip: {
                    backgroundColor: "white",
                    color: darkBlue,
                    boxShadow: theme.shadows[1],
                    borderRadius: 10,
                    fontSize: theme.typography.pxToRem(14),
                    padding: theme.spacing(2),
                    maxHeight: 120,
                    maxWidth: 650,
                    letterSpacing: 0.01,
                },
            },
        },
        MuiTableSortLabel: {
            styleOverrides: {
                icon: {
                    fill: `${darkLavender} !important`,
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paperWidthMd: {
                    maxWidth: 1200,
                    width: 1200,
                },
                paperWidthSm: {
                    maxWidth: 750,
                    width: 750,
                },
                paperWidthXs: {
                    maxWidth: 380,
                    width: 380,
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                sizeSmall: {
                    fontSize: theme.typography.pxToRem(12),
                    padding: `${theme.spacing(1.5)} 0`,
                    borderColor: warmGrey,
                    paddingRight: theme.spacing(1.5),
                },
                root: {
                    borderColor: `${warmGrey} !important`,
                },
                head: {
                    paddingRight: `${theme.spacing(1.5)} !important`,
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                input: {
                    fontSize: theme.typography.pxToRem(14),
                    "&::placeholder": {
                        color: "#707070",
                        fontSize: theme.typography.pxToRem(14),
                    },
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    fontSize: theme.typography.pxToRem(14),
                },
                shrink: {
                    color: darkBlue,
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                adornedEnd: {
                    paddingRight: 0,
                },
                notchedOutline: {
                    borderColor: darkBlue,
                },
                root: {
                    borderRadius: 5,
                    "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: darkBlue,
                    },
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    color: "#707070",
                    fontSize: theme.typography.pxToRem(14),
                },
            },
        },
        MuiTableContainer: {
            styleOverrides: {
                root: {
                    overflowX: "unset",
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    display: "flex",
                    justifyContent: "center",
                    paddingTop: 40 - 8,
                    paddingBottom: theme.spacing(6.5),
                },
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    flexDirection: "column",
                    justifyContent: "center",
                    gap: 20,
                },
                spacing: {
                    padding: 40 - 8,
                },
            },
        },
        MuiTypography: {
            styleOverrides: {
                h6: {
                    fontWeight: theme.typography.fontWeightBold,
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                rounded: {
                    borderRadius: 20,
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    ...ghostButtonStyle,
                    border: "1px solid #D3D3D3",
                    minWidth: "130px",
                    fontWeight: theme.typography.fontWeightBold,
                    textTransform: "none",
                    "&.MuiButton-label": {
                        color: "inherit",
                    },
                    "&.Mui-disabled": {
                        color: "#B0B0B0",
                        backgroundColor: "#F0F0F0",
                        borderColor: "#E0E0E0",
                    },
                    borderRadius: "10px",
                },
                textPrimary: {
                    "&:hover": {
                        border: "none",
                        backgroundColor: white,
                    },
                },
                outlinedPrimary: ghostButtonStyle,
                containedPrimary: ghostButtonStyle,
                outlinedSecondary: {
                    borderColor: darkBlue,
                    backgroundColor: white,
                    color: darkBlue,
                    "&:hover": {
                        borderColor: "#333333",
                    },
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    color: darkBlue,
                },
            },
        },
        MuiToolbar: {
            styleOverrides: {
                root: {
                    backgroundColor: darkBlue,
                },
            },
        },
        // MuiPickersToolbar: {
        //   styleOverrides: {
        //     toolbar: {
        //       backgroundColor: '#E4EAFF',
        //     },
        //   },
        // },
        // MuiPickersModal: {},
        // MuiPickersToolbarButton: {
        //   styleOverrides: {
        //     toolbarBtn: {
        //       color: darkBlue,
        //       backgroundColor: '#E4EAFF',
        //     },
        //   },
        // },
        MuiSwitch: {
            styleOverrides: {
                switchBase: {
                    padding: 11,
                },
                track: {
                    backgroundColor: `${lavender} !important`,
                },
                thumb: {
                    height: 15,
                    width: 15,
                    backgroundColor: lavender,
                },
            },
        },
        MuiCollapse: {
            styleOverrides: {
                root: {
                    borderRadius: 10,
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                filledSuccess: {
                    background: `#6F7AC4 !important`,
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    fontSize: 14,
                    color: "black",
                },
            },
        },
    },
});

export default ipPortalTheme;
